"use client";

import { useEffect } from "react";
import { localePath } from "@/lib/i18n";
import {
  isCrawler,
  LOCALE_COOKIE,
  LOCALE_COOKIE_MAX_AGE,
  localeFromAcceptLanguage,
} from "@/lib/i18n/detect";
import { DEFAULT_LOCALE } from "@/lib/i18n/locales";
import { hasTranslation } from "@/lib/i18n/localized-routes";

/**
 * Offers a first-time visitor on an English page the same page in their language.
 *
 * This used to be a 302 issued by `src/proxy.ts` from `Accept-Language` and the
 * visitor's country. Deciding it on the server meant the response to `/en/apa`
 * depended on who was asking, which is precisely what a shared cache cannot hold: a
 * CDN keys on the URL, ignores `Vary`, and would have pinned one reader's language
 * onto everyone behind it. The site is otherwise entirely static, and that one
 * per-visitor decision was the only thing keeping every HTML response uncacheable at
 * the edge.
 *
 * So the decision now happens in the browser, from `navigator.languages` — the same
 * ranked list the `Accept-Language` header is built from — with the same rules:
 *
 *  - only on the English tree, and only for a visitor with no `ce_lang` cookie, so a
 *    reader who has chosen a language (or followed a "View in English" link, which
 *    sets the cookie) is never bounced;
 *  - only to a page that language actually has, so the visitor never lands on a
 *    308 back to where they started;
 *  - never for a crawler. Search engines run JavaScript; geo-routing them is how a
 *    site gets its English pages indexed as Spanish.
 *
 * The cookie is written *before* the navigation, so the offer is made at most once
 * per reader and can never loop. The country fallback the proxy had (`CF-IPCountry`)
 * is gone: a browser's language list is the better signal and a request-time country
 * is not available here.
 */
export function LanguageOffer({
  locale,
  englishPath,
}: {
  locale: string;
  /** Un-prefixed path of the current page, e.g. `/apa`. */
  englishPath: string;
}) {
  useEffect(() => {
    if (locale !== DEFAULT_LOCALE) {
      return;
    }
    if (typeof document === "undefined" || typeof navigator === "undefined") {
      return;
    }
    if (document.cookie.split(";").some(isLocaleCookie)) {
      return;
    }
    if (navigator.webdriver || isCrawler(navigator.userAgent)) {
      return;
    }
    const preferred = localeFromAcceptLanguage(navigator.languages.join(","));
    if (
      !preferred ||
      preferred === DEFAULT_LOCALE ||
      !hasTranslation(preferred, englishPath)
    ) {
      return;
    }
    // biome-ignore lint/suspicious/noDocumentCookie: the Cookie Store API is unavailable in Safari and every WebKit browser on iOS; a preference that silently fails to stick there would re-offer the redirect on every page.
    document.cookie = `${LOCALE_COOKIE}=${preferred}; path=/; max-age=${LOCALE_COOKIE_MAX_AGE}; samesite=lax`;
    window.location.replace(localePath(preferred, englishPath));
  }, [locale, englishPath]);

  return null;
}

function isLocaleCookie(part: string): boolean {
  return part.trim().startsWith(`${LOCALE_COOKIE}=`);
}
